import React, { useState } from 'react';
import { Modal, Button, Input } from 'glintly-ui';
import styles from './profile.module.css';
import { toast } from 'react-toastify';
import { useFileUpload } from '../../api/services';
const KYCModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    adhaarNo: '',
    adhaarFrontImg: '',
    adhaarbackImg: '',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // File upload hooks for each file input
  const frontImgUpload = useFileUpload({
    maxSize: 5 * 1024 * 1024, // 5MB
    allowedTypes: ['image/jpeg', 'image/jpg', 'image/png'],
    allowedExtensions: ['.jpg', '.jpeg', '.png'],
  });

  const backImgUpload = useFileUpload({
    maxSize: 5 * 1024 * 1024, // 5MB
    allowedTypes: ['image/jpeg', 'image/jpg', 'image/png'],
    allowedExtensions: ['.jpg', '.jpeg', '.png'],
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  // Handle file upload for front image
  const handleFrontImageUpload = async (e) => {
    // glintly-ui FileInput emits { target: { name, value: File[] | null } }
    // Fallback to native input event if present
    const filesFromLib = e?.target?.value;
    const filesFromNative = e?.target?.files;
    const files = Array.isArray(filesFromLib) ? filesFromLib : filesFromNative;
    const file = files && files[0];
    if (!file) return;

    try {
      const uploadedFile = await frontImgUpload.uploadFile(file);
      setFormData((prev) => ({
        ...prev,
        adhaarFrontImg: uploadedFile.url,
      }));
      toast.success('Front image uploaded successfully!');
    } catch (error) {
      toast.error(error.message || 'Failed to upload front image');
    }
  };

  // Handle file upload for back image
  const handleBackImageUpload = async (e) => {
    const filesFromLib = e?.target?.value;
    const filesFromNative = e?.target?.files;
    const files = Array.isArray(filesFromLib) ? filesFromLib : filesFromNative;
    const file = files && files[0];
    if (!file) return;

    try {
      const uploadedFile = await backImgUpload.uploadFile(file);
      setFormData((prev) => ({
        ...prev,
        adhaarbackImg: uploadedFile.url,
      }));
      toast.success('Back image uploaded successfully!');
    } catch (error) {
      toast.error(error.message || 'Failed to upload back image');
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.adhaarNo) {
      newErrors.adhaarNo = 'Aadhaar number is required';
    } else if (!/^\d{12}$/.test(formData.adhaarNo)) {
      newErrors.adhaarNo = 'Aadhaar number must be 12 digits';
    }

    if (!formData.adhaarFrontImg) {
      newErrors.adhaarFrontImg = 'Aadhaar front image is required';
    }

    if (!formData.adhaarbackImg) {
      newErrors.adhaarbackImg = 'Aadhaar back image is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log('KYC Modal handleSubmit called');
    console.log('onSubmit function:', typeof onSubmit);
    if (!validateForm()) {
      console.log('validateForm:', !validateForm());
      return;
    }

    if (!onSubmit || typeof onSubmit !== 'function') {
      console.error('onSubmit is not a function:', onSubmit);
      toast.error('Submit function not available');
      return;
    }

    setLoading(true);
    try {
      await onSubmit(formData);
      toast.success('KYC documents submitted successfully!');
      onClose();
      // Reset form
      setFormData({
        adhaarNo: '',
        adhaarFrontImg: '',
        adhaarbackImg: '',
      });
      setErrors({});
    } catch (error) {
      console.error('KYC Modal submission error:', error);
      toast.error(error.message || 'Failed to submit KYC documents');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      adhaarNo: '',
      adhaarFrontImg: '',
      adhaarbackImg: '',
    });
    setErrors({});
    // Reset upload states
    frontImgUpload.reset();
    backImgUpload.reset();
    onClose();
  };

  return (
    <Modal size="lg" isOpen={isOpen} onClose={handleClose}>
      <Modal.Header>Aadhaar Information</Modal.Header>
      <Modal.Body>
        <div className={styles.modalContent}>
          <form onSubmit={handleSubmit} className={styles.modalForm}>
            <div className={styles.formSection}>
              <div className={styles.formGroup}>
                <Input.Root name="adhaarNo" required errorMessage={errors.adhaarNo}>
                  <Input.Label>Aadhaar Number</Input.Label>
                  <Input.Control
                    type="text"
                    value={formData.adhaarNo}
                    onChange={handleInputChange}
                    placeholder="Enter 12-digit Aadhaar number"
                    maxLength={12}
                  />
                </Input.Root>
              </div>

              <div className={styles.formGroup}>
                <Input.Root name="adhaarFrontImg" errorMessage={errors.adhaarFrontImg}>
                  <Input.Label>Aadhaar Front Image</Input.Label>
                  <Input.Control
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    onChange={handleFrontImageUpload}
                    disabled={frontImgUpload.uploading}
                  />
                </Input.Root>
                {frontImgUpload.uploading && (
                  <div className={styles.uploadProgress}>
                    <div className={styles.progressBar}>
                      <div
                        className={styles.progressFill}
                        style={{ width: `${frontImgUpload.progress}%` }}
                      ></div>
                    </div>
                    <span>Uploading... {frontImgUpload.progress}%</span>
                  </div>
                )}
                {formData.adhaarFrontImg && (
                  <div className={styles.uploadedFile}>
                    <span>✓ Image uploaded successfully</span>
                    <img
                      src={formData.adhaarFrontImg}
                      alt="Front preview"
                      className={styles.imagePreview}
                    />
                  </div>
                )}
              </div>

              <div className={styles.formGroup}>
                <Input.Root name="adhaarbackImg" errorMessage={errors.adhaarbackImg}>
                  <Input.Label>Aadhaar Back Image</Input.Label>
                  <Input.Control
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    onChange={handleBackImageUpload}
                    disabled={backImgUpload.uploading}
                  />
                </Input.Root>
                {backImgUpload.uploading && (
                  <div className={styles.uploadProgress}>
                    <div className={styles.progressBar}>
                      <div
                        className={styles.progressFill}
                        style={{ width: `${backImgUpload.progress}%` }}
                      ></div>
                    </div>
                    <span>Uploading... {backImgUpload.progress}%</span>
                  </div>
                )}
                {formData.adhaarbackImg && (
                  <div className={styles.uploadedFile}>
                    <span>✓ Image uploaded successfully</span>
                    <img
                      src={formData.adhaarbackImg}
                      alt="Back preview"
                      className={styles.imagePreview}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className={styles.uploadInstructions}>
              <h4>Upload Instructions:</h4>
              <ul>
                <li>Upload clear, high-resolution images</li>
                <li>Ensure all text is readable</li>
                <li>Images should be in JPG, PNG, or PDF format</li>
                <li>Maximum file size: 5MB per image</li>
              </ul>
            </div>

            <div className={styles.modalActions}>
              <Button type="button" variant="transparent" onClick={handleClose} disabled={loading}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? 'Submitting...' : 'Submit KYC Documents'}
              </Button>
            </div>
          </form>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default KYCModal;
